import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { Badge, Box, Chip, Divider, Link, Popover, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useState } from 'react';

import { useAnalytics } from '~/analytics/useAnalytics';
import { DOCS_AND_GUIDES_LABEL, DOCS_AND_GUIDES_URL } from '~/components/docs/docsAndGuides';
import AppBarButton from '~/components/global/AppBarButton';
import { useWhatsNew } from '~/components/whatsNew/useWhatsNew';
import { WhatsNewDemoThumbnail } from '~/components/whatsNew/WhatsNewDemoThumbnail';
import { WhatsNewEntryTitle } from '~/components/whatsNew/WhatsNewEntryTitle';
import { WhatsNewShowcaseDialog } from '~/components/whatsNew/WhatsNewShowcaseDialog';
import { type ChangelogEntry } from '~/data/schemas/changelog';
import { SURFACE_RADIUS } from '~/theme';

export function WhatsNewBell({
  orientation = 'vertical',
}: {
  orientation?: 'vertical' | 'horizontal';
}) {
  const { entries, unreadEntries, markSeen } = useWhatsNew();
  const { track } = useAnalytics();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [demo, setDemo] = useState<ChangelogEntry | null>(null);

  const unreadCount = unreadEntries.length;
  const open = Boolean(anchorEl);

  // Mark seen while the popover is open rather than only on click, so entries that
  // arrive on a refresh while it is open don't leave the badge lit.
  useEffect(() => {
    if (open) markSeen();
  }, [open, markSeen]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Report the unread count as of the moment of opening (before marking seen).
    track('whats_new_opened', { unread_count: unreadCount });
  };

  const handleClose = () => setAnchorEl(null);

  const handleEntryClick = (entry: ChangelogEntry) => {
    track('whats_new_entry_clicked', { entry_id: entry.id, source: 'bell' });
  };

  const handleWatch = (entry: ChangelogEntry) => {
    track('whats_new_showcase_shown', { entry_count: 1, source: 'bell' });
    setAnchorEl(null);
    setDemo(entry);
  };

  // Nothing to show (fetch/parse error or empty changelog): render no chrome.
  if (entries.length === 0) return null;

  return (
    <>
      <AppBarButton
        label="What's New"
        aria-label="What's New"
        orientation={orientation}
        onClick={handleOpen}
        icon={
          <Badge color="error" variant="dot" badgeContent={unreadCount} overlap="circular">
            <NotificationsNoneIcon />
          </Badge>
        }
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          // The panel is a reading surface, not a menu, so it takes the panel radius rather than
          // the tighter one transient surfaces are pinned to.
          paper: { sx: { borderRadius: `${SURFACE_RADIUS}px`, mt: 0.5 } },
        }}
      >
        <Link
          href={DOCS_AND_GUIDES_URL}
          target="_blank"
          rel="noopener"
          underline="none"
          onClick={() => track('help_center_clicked', { source: 'whats_new' })}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            bgcolor: 'background.paper',
            backgroundImage: (theme) => tint(alpha(theme.palette.primary.main, 0.08)),
            borderBottom: 1,
            borderColor: 'divider',
            '&:hover': {
              backgroundImage: (theme) => tint(alpha(theme.palette.primary.main, 0.14)),
            },
          }}
        >
          <AutoStoriesOutlined color="primary" />
          <Box>
            <Typography
              variant="subtitle2"
              color="primary"
              sx={{ fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '3px' }}
            >
              {DOCS_AND_GUIDES_LABEL}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Step-by-step guides and demo videos for every feature
            </Typography>
          </Box>
          <NorthEastIcon color="primary" aria-hidden sx={{ fontSize: 16, ml: 'auto' }} />
        </Link>
        <Stack
          spacing={2}
          divider={<Divider flexItem />}
          sx={{
            p: 2.5,
            width: 'min(480px, calc(100vw - 32px))',
          }}
        >
          {entries.map((entry) => (
            <Box key={entry.id}>
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                sx={{ alignItems: 'center', flexWrap: 'wrap' }}
              >
                <Typography variant="caption" color="text.secondary">
                  {entry.date}
                </Typography>
                {entry.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ height: 20, typography: 'caption' }}
                  />
                ))}
              </Stack>
              <WhatsNewEntryTitle entry={entry} onClick={() => handleEntryClick(entry)} />
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {entry.summary}
              </Typography>
              {entry.showcase ? (
                <WhatsNewDemoThumbnail
                  video={entry.showcase.video}
                  poster={entry.showcase.poster}
                  title={entry.title}
                  onClick={() => handleWatch(entry)}
                />
              ) : null}
            </Box>
          ))}
        </Stack>
      </Popover>
      {demo ? (
        <WhatsNewShowcaseDialog
          entries={[demo]}
          autoPlay
          onGuideClick={(entry) =>
            track('whats_new_entry_clicked', { entry_id: entry.id, source: 'showcase' })
          }
          onClose={(_position, finished) => {
            track('whats_new_showcase_closed', {
              entry_id: demo.id,
              position: 1,
              total: 1,
              finished,
              source: 'bell',
            });
            setDemo(null);
          }}
        />
      ) : null}
    </>
  );
}

const tint = (color: string) => `linear-gradient(${color}, ${color})`;
